'use client'

import { KYCVerificationDialog } from '@/components/Account/KYCVerificationDialog'
import { useKYCDismissed } from '@/hooks/use-kyc-dismiss'
import { mixpanelClient } from '@/lib/analytics/mixpanelClient'
import { useKYCData } from '@/lib/queries/account'
import { getHasSubmittedUserDetailsToKYC } from '@shared/utils/kycState'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, use, useEffect, useState } from 'react'
import { useIsMounted } from 'usehooks-ts'

export type VerificationStep = 'intro' | 'kyc' | null

type KycContextType = {
  kycStep: VerificationStep
  handleOpenChange: (open: boolean) => void
  setKycStep: (step: VerificationStep) => void
}

const KycContext = createContext<KycContextType | undefined>(undefined)

export const KycProvider = ({ children }: { children: React.ReactNode }) => {
  const [kycStep, setKycStep] = useState<VerificationStep>(null)
  const [KYCDismissed, setKYCDismissed] = useKYCDismissed()
  const isMounted = useIsMounted()

  const { hasKYC, statuses, isPending } = useKYCData()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (statuses.status === 'Approved|Passed') {
      mixpanelClient.setOnce(
        'KYC Approved Client Side Event Date',
        new Date().toISOString(),
      )
    }
  }, [statuses.status])

  useEffect(() => {
    if (!isMounted || KYCDismissed || isPending) {
      return
    }

    const hasSubmittedUserDetailsToKYC =
      hasKYC || getHasSubmittedUserDetailsToKYC(statuses)

    if (!hasSubmittedUserDetailsToKYC && !kycStep) {
      setKycStep('intro')
    }
  }, [isMounted, KYCDismissed, kycStep, hasKYC, statuses, isPending])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      setKYCDismissed(true)
      setKycStep(null)
      return
    }
    setKycStep('intro')
  }

  return (
    <KycContext
      value={{
        kycStep,
        setKycStep,
        handleOpenChange,
      }}
    >
      {children}
      <KYCVerificationDialog />
    </KycContext>
  )
}

export const useKycContext = () => {
  const context = use(KycContext)
  if (context === undefined) {
    throw new Error('useKycContext must be used within a KycProvider')
  }
  return context
}
